import { HttpClient, getApiBaseUrl } from "@maeiz/core";
import { useAuthStore } from "@stores/auth/authStore";
import { AxiosHeaders, type InternalAxiosRequestConfig } from "axios";

export const httpClient = new HttpClient(getApiBaseUrl());

httpClient.setRequestInterceptor((config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token;
    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData && config.headers) {
        if (config.headers instanceof AxiosHeaders) {
            config.headers.setContentType(false);
        } else {
            delete config.headers["Content-Type"];
        }
    }

    return config;
});

// A 401 on any non-auth route: try one token refresh and replay the request (the core client
// does the replay + single-flight). If the refresh also fails the session is dropped.
httpClient.setUnauthorizedHandler(async () => {
    const { token, user } = useAuthStore.getState();
    if (!token || !user) {
        useAuthStore.getState().clearAuth();
        return null;
    }
    try {
        const res = (await httpClient.post("/auth/refresh-token")) as unknown as {
            accessToken?: string;
            token?: string;
            billingOnly?: boolean;
        };
        const next = res.accessToken || res.token;
        if (next) {
            useAuthStore.getState().setAuth(user, next, { billingOnly: res.billingOnly });
            return next;
        }
    } catch {
        /* refresh failed — fall through and end the session */
    }
    useAuthStore.getState().clearAuth();
    return null;
});

// Auth routes are excluded from refresh-and-retry by the core client, so a rejected session
// check has to end the session here. A 401 from /auth/login (wrong password) must not.
httpClient.setResponseInterceptor(
    (response) => response,
    (error: unknown) => {
        const err = error as {
            response?: { status?: number; data?: { error?: { code?: string } } };
            config?: { url?: string };
        };
        const url = String(err?.config?.url ?? "");
        const status = err?.response?.status;
        if (status === 401 && /\/auth\/(me|check-auth)/.test(url)) {
            useAuthStore.getState().clearAuth();
        }
        // The account was locked to billing (non-payment) while the app was open.
        if (status === 403 && err?.response?.data?.error?.code === "BILLING_ONLY") {
            useAuthStore.getState().setBillingOnly(true);
        }
        return Promise.reject(error);
    }
);
